/**
 * Template source helpers (EXE-233 — combined standard + custom template list).
 *
 * The create-order Order info step (Step1.0-OrderInformation) renders a single
 * combined list of Bimify "standard" templates and the customer's own "custom"
 * templates, each tagged with a source label.
 *
 * EXE-221 dependency: the BE does not yet expose an explicit standard/custom
 * discriminator on the `/revit-file` payload. Until it does, the source is
 * derived from company ownership (a template with no owning company is a Bimify
 * global / standard template; one owned by the selected customer is custom).
 * `getTemplateSource` is written to be forward compatible: if/when EXE-221 adds
 * an explicit field (`source`, `isStandard` or `isCustom`), that field wins and
 * the ownership heuristic is skipped — no call-site change required.
 */

export const templateSourceConstants = {
	STANDARD: "STANDARD",
	CUSTOM: "CUSTOM",
};

/**
 * Resolve a template's source label.
 *
 * @param {Object} template Plain object describing the template. Recognised
 *   fields: `source` ("STANDARD"|"CUSTOM"), `isStandard` (bool), `isCustom`
 *   (bool) and `companyId` (owning company id, null/undefined for Bimify
 *   global templates).
 * @param {string|number} selectedCustomerId The id of the customer the order is
 *   being created for. A template owned by this customer is "custom".
 * @returns {string} One of `templateSourceConstants`.
 */
export const getTemplateSource = (template = {}, selectedCustomerId) => {
	const { source, isStandard, isCustom, companyId } = template;

	// 1. Explicit-field precedence (forward-compat with EXE-221).
	if (
		source === templateSourceConstants.STANDARD ||
		source === templateSourceConstants.CUSTOM
	) {
		return source;
	}
	if (isStandard === true) {
		return templateSourceConstants.STANDARD;
	}
	if (isCustom === true) {
		return templateSourceConstants.CUSTOM;
	}

	// 2. Derive from ownership.
	// No owning company → Bimify global / standard template.
	if (companyId === null || companyId === undefined) {
		return templateSourceConstants.STANDARD;
	}
	// Owned by the selected customer → custom. Anything else (owned by Bimify
	// or another party) is standard from this customer's perspective — a safe
	// default that never mislabels a foreign template as custom.
	if (String(companyId) === String(selectedCustomerId)) {
		return templateSourceConstants.CUSTOM;
	}

	return templateSourceConstants.STANDARD;
};

/**
 * Build a combined standard + custom template option list, sorted STANDARD
 * first then alphabetically within each group. Shared across every surface
 * that lets a user pick a Revit template (order create/edit, job
 * create/edit, model-update) so they all render the same source-tagged
 * list — see `renderTemplateOption` (components/fields/renderTemplateOption)
 * for the matching row renderer.
 *
 * @param {Immutable.List} revitTemplateList List of template Immutable Maps
 *   (fields: `id`, `name`, `source`/`isStandard`/`isCustom`, `company.id`).
 * @param {string|number} selectedCustomerId The customer id used to derive
 *   which templates are "custom" (see `getTemplateSource`).
 * @returns {Array<{label: string, value: *, source: string}>}
 */
export const buildTemplateOptions = (revitTemplateList, selectedCustomerId) => {
	const options = [];

	revitTemplateList &&
		revitTemplateList.forEach((item) => {
			const source = getTemplateSource(
				{
					source: item.get("source"),
					isStandard: item.get("isStandard"),
					isCustom: item.get("isCustom"),
					companyId: item.getIn(["company", "id"]),
				},
				selectedCustomerId
			);

			options.push({
				label: item.get("name"),
				value: item.get("id"),
				source,
			});
		});

	// STANDARD before CUSTOM, then alphabetical by label within each group.
	options.sort((a, b) => {
		if (a.source !== b.source) {
			return a.source === templateSourceConstants.STANDARD ? -1 : 1;
		}

		return a.label.localeCompare(b.label);
	});

	return options;
};
